import { Combobox, type ComboboxOption } from '@ordo/ui/combobox'
import { AsyncCombobox } from 'compositions/AsyncCombobox'
import { useContactGroups } from 'hooks/api'
import { useAsyncLoader } from 'hooks/useAsyncLoader'
import { X } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import type { Contact } from 'types/contacts'

export interface Recipient {
  type: 'contact' | 'group'
  id: string
  label: string
  /** Contact email at the time of selection — only set for `type: 'contact'`. */
  email?: string | null
  /** Contact phone at the time of selection — only set for `type: 'contact'`. */
  phoneNumber?: string | null
}

function recipientKey(r: Recipient): string {
  return `${r.type}:${r.id}`
}

function contactLabel(c: Contact): string {
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || '(unnamed)'
  return c.email ? `${name} (${c.email})` : name
}

export function RecipientSelector({
  recipients,
  onAdd,
  onRemove,
}: {
  recipients: Recipient[]
  onAdd: (recipient: Recipient) => void
  onRemove: (recipient: Recipient) => void
}) {
  const loadContacts = useAsyncLoader<Contact>('/api/contacts', { active: true })
  const { data: groups } = useContactGroups()

  const selectedKeys = useMemo(() => new Set(recipients.map(recipientKey)), [recipients])

  const handleAddContact = useCallback(
    (contact: Contact | null) => {
      if (!contact || selectedKeys.has(`contact:${contact.id}`)) {
        return
      }
      onAdd({
        type: 'contact',
        id: contact.id,
        label: contactLabel(contact),
        email: contact.email,
        phoneNumber: contact.phoneNumber,
      })
    },
    [onAdd, selectedKeys],
  )

  const groupOptions: ComboboxOption[] = useMemo(
    () => (groups ?? []).filter((g) => !selectedKeys.has(`group:${g.id}`)).map((g) => ({ value: g.id, label: g.name })),
    [groups, selectedKeys],
  )

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <AsyncCombobox<Contact>
          value={null}
          onChange={handleAddContact}
          load={loadContacts}
          getKey={(c) => c.id}
          getLabel={contactLabel}
          getSublabel={(c) => c.email ?? c.phoneNumber ?? ''}
          placeholder="Add a contact…"
          searchPlaceholder="Search contacts by name, email, or phone…"
          emptyMessage="No contacts found"
        />
        <Combobox
          options={groupOptions}
          value=""
          resetOnSelect
          onValueChange={(v) => {
            const group = (groups ?? []).find((g) => g.id === v)
            if (group) {
              onAdd({ type: 'group', id: group.id, label: group.name })
            }
          }}
          placeholder="Add a group…"
          searchPlaceholder="Search groups…"
          emptyMessage="No groups available"
        />
      </div>

      {/* Selected recipients as bubbles */}
      {recipients.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {recipients.map((r) => (
            <span
              key={recipientKey(r)}
              className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground">
              <span className="opacity-60">{r.type === 'group' ? 'Group:' : ''}</span>
              {r.label}
              <button
                type="button"
                onClick={() => onRemove(r)}
                className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-destructive/20 hover:text-destructive">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
